import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

import AmountInput from '../components/AmountInput'
import DatePicker from '../components/DatePicker'
import ExpenseTypePicker from '../components/ExpenseTypePicker'
import { paymentAPI, walletAPI, expenseAPI, expenseTypeAPI } from '../services/api'
import { formatCurrency } from '../common/currency'
import { toDateInputValue, formatDate } from '../common/date'
import { useI18n } from '../contexts/I18nContext'
import type { Wallet } from '../types/wallet'
import type { Expense, ExpenseType } from '../types/expense'

interface NewExpenseEntry {
  tempId: number
  expense_type_id: string
  amount: string
  date: string
  note: string
}

interface PaymentDraftFormState {
  wallet_id: string
  amount: string
  date: string
  note: string
}

interface PaymentDraftState {
  form: PaymentDraftFormState
  selectedExpenseIds: number[]
  newExpenses: NewExpenseEntry[]
  amountMode: AmountMode
  discrepancyMode: DiscrepancyMode
}

interface PaymentFormLocationState {
  returnTo?: string
  paymentDraft?: PaymentDraftState
}

type AmountMode = 'expenses-total' | 'manual'
type DiscrepancyMode = 'default-expense' | 'unexplained'

function mergeExpenses(linkedExpenses: Expense[], unbilledExpenses: Expense[]): Expense[] {
  const map = new Map<number, Expense>()
  for (const expense of linkedExpenses) {
    map.set(expense.id, expense)
  }
  for (const expense of unbilledExpenses) {
    map.set(expense.id, expense)
  }
  return Array.from(map.values())
}

function formatAmountInput(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) {
    return ''
  }
  return Math.round(amount).toString()
}

export default function PaymentFormPage() {
  const { t } = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const presetWalletId = searchParams.get('wallet_id') ?? ''
  const presetDate = searchParams.get('date') ?? ''
  const navigationState = location.state as PaymentFormLocationState | null
  const returnTo = !isEdit ? navigationState?.returnTo ?? '/payments' : '/payments'
  const returnPath = `${location.pathname}${location.search}`

  const [form, setForm] = useState({
    wallet_id: '' as string,
    amount: '',
    date: toDateInputValue(new Date()),
    note: '',
  })
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [unbilledExpenses, setUnbilledExpenses] = useState<Expense[]>([])
  const [linkedExpenses, setLinkedExpenses] = useState<Expense[]>([])
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<number[]>([])
  const [newExpenses, setNewExpenses] = useState<NewExpenseEntry[]>([])
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEdit)
  const [error, setError] = useState('')
  const [walletPickerOpen, setWalletPickerOpen] = useState(!isEdit)
  const [amountMode, setAmountMode] = useState<AmountMode>(isEdit ? 'manual' : 'expenses-total')
  const [discrepancyMode, setDiscrepancyMode] = useState<DiscrepancyMode>('unexplained')

  const selectedWallet = wallets.find((w) => w.id === Number(form.wallet_id))
  const allExpenses = mergeExpenses(linkedExpenses, unbilledExpenses)
  const linkedTotal = allExpenses
    .filter((expense) => selectedExpenseIds.includes(expense.id))
    .reduce((sum, expense) => sum + expense.amount, 0)
  const validNewExpenses = newExpenses.filter((expense) => {
    const amount = Number(expense.amount)
    return expense.expense_type_id && expense.amount.trim() !== '' && Number.isFinite(amount) && amount >= 0
  })
  const newTotal = validNewExpenses.reduce((sum, expense) => sum + Number(expense.amount), 0)
  const expenseGrandTotal = Math.round(linkedTotal + newTotal)
  const paymentAmount = Number(form.amount) || 0
  const discrepancy = paymentAmount - expenseGrandTotal
  const hasExpenses = selectedExpenseIds.length > 0 || newExpenses.length > 0
  const canCreateDefaultDiscrepancyExpense = discrepancy > 0 && Boolean(selectedWallet?.default_expense_type_id) && !selectedWallet?.is_cash
  const defaultExpenseType = selectedWallet?.default_expense_type_id
    ? expenseTypes.find((type) => type.id === selectedWallet.default_expense_type_id)
    : undefined
  const parsedAmount = form.amount.trim() === '' ? null : Number(form.amount)
  const canSubmit = Boolean(
    !loading
    && form.wallet_id
    && parsedAmount !== null
    && Number.isFinite(parsedAmount)
    && parsedAmount >= 0,
  )

  useEffect(() => {
    const paymentDraft = navigationState?.paymentDraft
    if (!paymentDraft) return

    setForm(paymentDraft.form)
    setSelectedExpenseIds(paymentDraft.selectedExpenseIds)
    setNewExpenses(
      paymentDraft.newExpenses.map((expense) => ({
        ...expense,
        date: expense.date || paymentDraft.form.date,
      })),
    )
    setAmountMode(paymentDraft.amountMode)
    setDiscrepancyMode(paymentDraft.discrepancyMode)
    setWalletPickerOpen(!paymentDraft.form.wallet_id)
    if (isEdit) {
      setInitialLoading(false)
    }
  }, [isEdit, navigationState])

  useEffect(() => {
    walletAPI
      .list({ includeStopped: false })
      .then((r) => setWallets(r.wallets))
      .catch((err) => { console.error(err) })
    expenseTypeAPI
      .list({ includeStopped: false })
      .then((r) => setExpenseTypes(r.expense_types))
      .catch((err) => { console.error(err) })
  }, [])

  useEffect(() => {
    if (isEdit || navigationState?.paymentDraft) return
    setForm((prev) => ({
      ...prev,
      wallet_id: presetWalletId || prev.wallet_id,
      date: presetDate || prev.date,
    }))
    if (presetWalletId) {
      setWalletPickerOpen(false)
    }
  }, [isEdit, navigationState, presetDate, presetWalletId])

  // Load unbilled expenses when wallet selected
  useEffect(() => {
    if (!selectedWallet) {
      setUnbilledExpenses([])
      setSelectedExpenseIds([])
      return
    }
    walletAPI
      .getUnbilledExpenses(selectedWallet.id)
      .then((r) => setUnbilledExpenses(r.expenses))
      .catch(() => setUnbilledExpenses([]))
  }, [selectedWallet])

  useEffect(() => {
    if (!isEdit || !id || navigationState?.paymentDraft) return
    setInitialLoading(true)
    paymentAPI
      .get(Number(id))
      .then((p) => {
        setForm({
          wallet_id: p.wallet_id.toString(),
          amount: Math.round(p.amount).toString(),
          date: toDateInputValue(p.date),
          note: p.note,
        })
        setAmountMode('manual')
        setWalletPickerOpen(false)
        // Load existing linked expenses for edit
        return expenseAPI.list({ paymentId: p.id })
      })
      .then((r) => {
        if (r.expenses.length > 0) {
          setLinkedExpenses(r.expenses)
          setSelectedExpenseIds(r.expenses.map((e) => e.id))
        }
      })
        .catch(() => setError(t('Failed to load payment')))
      .finally(() => setInitialLoading(false))
      }, [id, isEdit, navigationState, t])

  useEffect(() => {
    if (amountMode !== 'expenses-total') {
      return
    }

    const nextAmount = formatAmountInput(expenseGrandTotal)
    setForm((prev) => (prev.amount === nextAmount ? prev : { ...prev, amount: nextAmount }))
  }, [amountMode, expenseGrandTotal])

  useEffect(() => {
    if (canCreateDefaultDiscrepancyExpense && discrepancyMode === 'unexplained' && !hasExpenses) {
      setDiscrepancyMode('default-expense')
      return
    }

    if (!canCreateDefaultDiscrepancyExpense && discrepancyMode === 'default-expense') {
      setDiscrepancyMode('unexplained')
    }
  }, [canCreateDefaultDiscrepancyExpense, discrepancyMode, hasExpenses])

  function toggleExpense(expenseId: number) {
    setSelectedExpenseIds((prev) =>
      prev.includes(expenseId) ? prev.filter((i) => i !== expenseId) : [...prev, expenseId],
    )
  }

  function selectAllExpenses() {
    setSelectedExpenseIds(allExpenses.map((e) => e.id))
  }

  function deselectAllExpenses() {
    setSelectedExpenseIds([])
  }

  function handleWalletSelect(walletId: string) {
    if (walletId === form.wallet_id) {
      setWalletPickerOpen(false)
      return
    }

    setForm((prev) => ({ ...prev, wallet_id: walletId }))
    setLinkedExpenses([])
    setUnbilledExpenses([])
    setSelectedExpenseIds([])
    setNewExpenses([])
    setDiscrepancyMode('unexplained')
    if (!isEdit) {
      setAmountMode('expenses-total')
    }
    setWalletPickerOpen(false)
  }

  function handleCreateWallet() {
    navigate('/wallets/new', {
      state: {
        returnTo: returnPath,
        paymentReturnTo: returnTo,
        paymentIsEdit: isEdit,
        paymentDraft: {
          form,
          selectedExpenseIds,
          newExpenses,
          amountMode,
          discrepancyMode,
        },
      },
    })
  }

  function addNewExpense() {
    const defaultTypeId = selectedWallet?.default_expense_type_id
    const entry: NewExpenseEntry = {
      tempId: Date.now(),
      expense_type_id: defaultTypeId ? defaultTypeId.toString() : '',
      amount: '',
      date: form.date,
      note: '',
    }
    // If wallet has a default expense type, pre-fill amount from it
    if (defaultTypeId) {
      const et = expenseTypes.find((t) => t.id === defaultTypeId)
      if (et?.default_amount) {
        entry.amount = Math.round(et.default_amount).toString()
      }
    }
    setNewExpenses((prev) => [...prev, entry])
  }

  function updateNewExpense(tempId: number, field: keyof Omit<NewExpenseEntry, 'tempId'>, value: string) {
    setNewExpenses((prev) =>
      prev.map((e) => (e.tempId === tempId ? { ...e, [field]: value } : e)),
    )
  }

  function removeNewExpense(tempId: number) {
    setNewExpenses((prev) => prev.filter((e) => e.tempId !== tempId))
  }

  function handleTypeSelect(tempId: number, typeId: string) {
    updateNewExpense(tempId, 'expense_type_id', typeId)
    const expenseType = expenseTypes.find((type) => type.id === Number(typeId))
    const entry = newExpenses.find((item) => item.tempId === tempId)
    if (expenseType?.default_amount && entry && !entry.amount) {
      updateNewExpense(tempId, 'amount', Math.round(expenseType.default_amount).toString())
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      // Create new expenses first, collect their IDs
      const createdIds: number[] = []
      for (const ne of validNewExpenses) {
        const created = await expenseAPI.create({
          expense_type_id: Number(ne.expense_type_id),
          wallet_id: Number(form.wallet_id),
          amount: Number(ne.amount),
          date: ne.date,
          note: ne.note || undefined,
        })
        createdIds.push(created.id)
      }

      if (canCreateDefaultDiscrepancyExpense && discrepancyMode === 'default-expense' && selectedWallet?.default_expense_type_id) {
        const created = await expenseAPI.create({
          expense_type_id: selectedWallet.default_expense_type_id,
          wallet_id: Number(form.wallet_id),
          amount: discrepancy,
          date: form.date,
          note: form.note || undefined,
        })
        createdIds.push(created.id)
      }

      const allExpenseIds = [...selectedExpenseIds, ...createdIds]
      const payload = {
        wallet_id: Number(form.wallet_id),
        amount: Number(form.amount),
        date: form.date,
        note: form.note || undefined,
        expense_ids: allExpenseIds.length > 0 ? allExpenseIds : undefined,
        auto_create_default_expense: false,
      }
      if (isEdit && id) {
        await paymentAPI.update(Number(id), payload)
      } else {
        await paymentAPI.create(payload)
      }
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to save payment'))
    } finally {
      setLoading(false)
    }
  }

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleReturn(): void {
    navigate(returnTo, { replace: true })
  }

  if (initialLoading) {
    return (
      <div className="page">
        <div className="page__loading"><div className="loading-orb" /></div>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page__header page__header--with-back">
        <button className="back-button" onClick={handleReturn}>
          <ArrowLeftIcon />
        </button>
        <div>
          <h1>{isEdit ? t('Edit Payment') : t('New Payment')}</h1>
          <p>{isEdit ? t('Update payment details') : t('Record a wallet payment')}</p>
        </div>
      </div>

      <form className="form-card" onSubmit={handleSubmit}>
        {error && <div className="alert alert--error">{error}</div>}

        {/* Wallet */}
        <div className="field">
          <div className="field__label-row">
            <label className="field__label">{t('Wallet')} *</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {selectedWallet && !walletPickerOpen && (
                <button type="button" className="link-button" onClick={() => setWalletPickerOpen(true)}>
                  {t('Select other')}
                </button>
              )}
              <button type="button" className="link-button" onClick={handleCreateWallet}>
                {t('Add new wallet')}
              </button>
            </div>
          </div>
          {selectedWallet && !walletPickerOpen ? (
            <div className="wallet-select-grid">
              <button
                type="button"
                className="wallet-select-item wallet-select-item--active"
                onClick={() => setWalletPickerOpen(true)}
              >
                <span className="wallet-select-item__icon" style={{ background: selectedWallet.color || '#577590' }}>
                  {selectedWallet.icon || selectedWallet.name.charAt(0).toUpperCase()}
                </span>
                <span className="wallet-select-item__name">{selectedWallet.name}</span>
                {(selectedWallet.is_credit || selectedWallet.is_cash) && (
                  <span className="wallet-select-item__badge">
                    {selectedWallet.is_credit ? t('Credit') : t('Cash')}
                  </span>
                )}
              </button>
            </div>
          ) : (
            <div className="wallet-select-grid">
              {wallets.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  className={`wallet-select-item${form.wallet_id === w.id.toString() ? ' wallet-select-item--active' : ''}`}
                  onClick={() => handleWalletSelect(w.id.toString())}
                >
                  <span className="wallet-select-item__icon" style={{ background: w.color || '#577590' }}>
                    {w.icon || w.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="wallet-select-item__name">{w.name}</span>
                  {(w.is_credit || w.is_cash) && (
                    <span className="wallet-select-item__badge">
                      {w.is_credit ? t('Credit') : t('Cash')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <p className="field-hint">{t('Choose the wallet this payment belongs to')}</p>
        </div>

        {/* Date & Amount */}
        <div className="field-row">
          <div className="field field--flex1">
            <label className="field__label">{t('Date')} *</label>
            <DatePicker
              value={form.date}
              onChange={(value) => set('date', value)}
              required
            />
          </div>
          <div className="field field--flex1">
            <div className="field__label-row">
              <label className="field__label">{t('Amount')} *</label>
              {hasExpenses && amountMode === 'manual' && (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setAmountMode('expenses-total')}
                >
                  {t('Use expenses total')}
                </button>
              )}
            </div>
            <AmountInput
              value={form.amount}
              onChange={(value) => {
                setAmountMode('manual')
                set('amount', value)
              }}
              placeholder="0"
              title="Payment amount"
              hint={hasExpenses ? t('Expenses total {amount}', { amount: formatCurrency(expenseGrandTotal) }) : undefined}
            />
            {hasExpenses && (
              <p className="field-hint">
                {t('Expenses total {amount}', { amount: formatCurrency(expenseGrandTotal) })}
                {amountMode === 'expenses-total' ? ` · ${t('amount tracks expense changes')}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Note */}
        <div className="field">
          <label className="field__label">{t('Note')}</label>
          <input
            className="field__input"
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            placeholder={t('Optional note')}
          />
        </div>

        {/* Linked Expenses Section */}
        {selectedWallet && (
          <div className="field">
            <div className="field__label-row">
              <label className="field__label">{t('Expenses')}</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {allExpenses.length > 0 && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={selectedExpenseIds.length === allExpenses.length ? deselectAllExpenses : selectAllExpenses}
                  >
                    {selectedExpenseIds.length === allExpenses.length ? t('Deselect all') : t('Select all')}
                  </button>
                )}
                <button type="button" className="link-button" onClick={addNewExpense}>
                  <PlusIcon style={{ width: '0.875rem', height: '0.875rem' }} /> {t('Add new')}
                </button>
              </div>
            </div>

            {/* Existing expenses (linked + unbilled) */}
            {allExpenses.length > 0 && (
              <div className="expense-checklist">
                {allExpenses.map((exp) => (
                  <label key={exp.id} className="expense-checklist__item">
                    <input
                      type="checkbox"
                      checked={selectedExpenseIds.includes(exp.id)}
                      onChange={() => toggleExpense(exp.id)}
                    />
                    <span className="expense-checklist__info">
                      <span>{exp.expense_type?.icon} {exp.expense_type?.name ?? t('Expense')}</span>
                      <small>{formatDate(exp.date)}{exp.note ? ` · ${exp.note}` : ''}</small>
                    </span>
                    <span className="expense-checklist__amount">{formatCurrency(exp.amount)}</span>
                  </label>
                ))}
              </div>
            )}

            {/* New expense entries */}
            {newExpenses.map((ne) => {
              return (
                <div key={ne.tempId} className="new-expense-row">
                  <ExpenseTypePicker
                    expenseTypes={expenseTypes}
                    selectedTypeId={ne.expense_type_id}
                    onSelect={(typeId) => handleTypeSelect(ne.tempId, typeId)}
                    placeholder="Expense Type"
                    title="Select Expense Type"
                    triggerClassName="new-expense-row__type"
                  />
                  <AmountInput
                    triggerClassName="new-expense-row__amount"
                    value={ne.amount}
                    onChange={(value) => updateNewExpense(ne.tempId, 'amount', value)}
                    placeholder={t('Amount')}
                    title="Expense amount"
                  />
                  <DatePicker
                    className="new-expense-row__date"
                    value={ne.date}
                    onChange={(value) => updateNewExpense(ne.tempId, 'date', value)}
                    aria-label={t('Expense date')}
                  />
                  <input
                    className="field__input new-expense-row__note"
                    placeholder={t('Note')}
                    value={ne.note}
                    onChange={(e) => updateNewExpense(ne.tempId, 'note', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn--icon btn--ghost"
                    onClick={() => removeNewExpense(ne.tempId)}
                    title={t('Remove')}
                  >
                    <TrashIcon />
                  </button>
                </div>
              )
            })}

            {hasExpenses && (
              <p className="field-hint">
                {selectedExpenseIds.length > 0 &&
                  t('{count} linked', { count: selectedExpenseIds.length })}
                {selectedExpenseIds.length > 0 && newExpenses.length > 0 && ' + '}
                {newExpenses.length > 0 &&
                  t('{count} new', { count: newExpenses.length })}
                {' · '}{t('Total:')} {formatCurrency(expenseGrandTotal)}
              </p>
            )}

            {form.amount && discrepancy !== 0 && (
              <div className="alert alert--info">
                <strong>
                  {discrepancy > 0
                    ? t('Payment exceeds expenses by {amount}', { amount: formatCurrency(discrepancy) })
                    : t('Expenses exceed payment by {amount}', { amount: formatCurrency(Math.abs(discrepancy)) })}
                </strong>
                {canCreateDefaultDiscrepancyExpense ? (
                  <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <input
                        type="radio"
                        name="payment-discrepancy"
                        checked={discrepancyMode === 'default-expense'}
                        onChange={() => setDiscrepancyMode('default-expense')}
                      />
                      <span>
                        {t('Add {name} for {amount}', { name: defaultExpenseType?.name ?? t('default expense'), amount: formatCurrency(discrepancy) })}
                      </span>
                    </label>
                    <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <input
                        type="radio"
                        name="payment-discrepancy"
                        checked={discrepancyMode === 'unexplained'}
                        onChange={() => setDiscrepancyMode('unexplained')}
                      />
                      <span>{t('Leave the discrepancy unexplained')}</span>
                    </label>
                  </div>
                ) : (
                  <p style={{ marginTop: '0.75rem' }}>
                    {discrepancy > 0 && selectedWallet?.default_expense_type_id == null
                      ? t('This wallet has no default expense type, so the difference will stay unexplained.')
                      : t('This difference will stay unexplained unless you adjust the payment amount or expense list.')}
                  </p>
                )}
              </div>
            )}

            {!hasExpenses && allExpenses.length === 0 && (
              <p className="field-hint">{t('No unbilled expenses for this wallet. Add new ones above.')}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={!canSubmit}
          >
            {loading ? t('Saving…') : isEdit ? t('Update Payment') : t('Create Payment')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={handleReturn}
          >
            {t('Cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}
